import React, { useMemo } from 'react';
import { Alert, Button, Modal, Space, message } from 'antd';
import ApprovalProgressTimeline from './ApprovalProgressTimeline';
import { useWfStepAssigneeTree, validateWfStepAssignees } from './WorkflowStepAssigneePicker';
import './ProcessDetailContent.css';

function PreviewSection({
  title,
  process,
  initiatorSelectSteps,
  wfStepAssignees,
  onWfStepAssigneesChange,
  confirmLoading,
  treeData,
  userMap,
  loading,
  error,
  className
}) {
  const assigneePick = useMemo(() => {
    if (!initiatorSelectSteps?.length) return null;
    return {
      enabled: true,
      value: wfStepAssignees,
      onChange: onWfStepAssigneesChange,
      treeData,
      userMap,
      loading,
      disabled: confirmLoading
    };
  }, [
    initiatorSelectSteps?.length,
    wfStepAssignees,
    onWfStepAssigneesChange,
    treeData,
    userMap,
    loading,
    confirmLoading
  ]);

  if (!process) return null;

  return (
    <div className={['process-detail-section', 'process-submit-preview-section', className].filter(Boolean).join(' ')}>
      {title ? <h4 className="process-detail-section-title process-submit-preview-section-title">{title}</h4> : null}
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 12 }} showIcon />
      )}
      <ApprovalProgressTimeline
        process={process}
        assigneePick={assigneePick}
      />
    </div>
  );
}

export default function ProcessSubmitPreviewModal({
  open,
  previewProcess,
  confirmLoading = false,
  cancelText = '取消',
  title = '确认提交审批',
  confirmText = '确认提交',
  initiatorSelectSteps = [],
  wfStepAssignees = {},
  onWfStepAssigneesChange,
  linkedPreview,
  onCancel,
  onConfirm
}) {
  const { treeData, userMap, loading, error } = useWfStepAssigneeTree();

  const handleConfirm = () => {
    if (initiatorSelectSteps.length) {
      const err = validateWfStepAssignees(initiatorSelectSteps, wfStepAssignees);
      if (err) {
        message.warning(err);
        return;
      }
    }
    if (linkedPreview?.initiatorSelectSteps?.length) {
      const err = validateWfStepAssignees(
        linkedPreview.initiatorSelectSteps,
        linkedPreview.wfStepAssignees || {}
      );
      if (err) {
        message.warning(err);
        return;
      }
    }
    onConfirm?.(wfStepAssignees);
  };

  const hasDualPreview = Boolean(linkedPreview?.process);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={hasDualPreview ? 960 : 720}
      footer={(
        <Space>
          <Button onClick={onCancel}>{cancelText}</Button>
          <Button type="primary" loading={confirmLoading} onClick={handleConfirm}>{confirmText}</Button>
        </Space>
      )}
      destroyOnHidden
    >
      {hasDualPreview ? (
        <div className="process-submit-preview-dual">
          <PreviewSection
            className="process-submit-preview-column"
            title="预付款/借款核销"
            process={previewProcess}
            initiatorSelectSteps={initiatorSelectSteps}
            wfStepAssignees={wfStepAssignees}
            onWfStepAssigneesChange={onWfStepAssigneesChange}
            confirmLoading={confirmLoading}
            treeData={treeData}
            userMap={userMap}
            loading={loading}
            error={null}
          />
          <PreviewSection
            className="process-submit-preview-column"
            title={linkedPreview.title || '追加报销'}
            process={linkedPreview.process}
            initiatorSelectSteps={linkedPreview.initiatorSelectSteps || []}
            wfStepAssignees={linkedPreview.wfStepAssignees || {}}
            onWfStepAssigneesChange={linkedPreview.onWfStepAssigneesChange}
            confirmLoading={confirmLoading}
            treeData={treeData}
            userMap={userMap}
            loading={loading}
            error={error}
          />
        </div>
      ) : (
        <PreviewSection
          process={previewProcess}
          initiatorSelectSteps={initiatorSelectSteps}
          wfStepAssignees={wfStepAssignees}
          onWfStepAssigneesChange={onWfStepAssigneesChange}
          confirmLoading={confirmLoading}
          treeData={treeData}
          userMap={userMap}
          loading={loading}
          error={error}
        />
      )}
    </Modal>
  );
}
